import { observer } from 'mobx-react-lite'
import type { LegendPreviewViewModel } from '@/features/legends/preview/LegendPreviewViewModel'
import {
  PreviewLine,
  PreviewMatch,
  PreviewMatchList,
  PreviewNotice,
  PreviewOutput,
  PreviewStat,
  PreviewValueText,
} from '@/features/legends/preview/components/PreviewOutput'
import { dateFormatters } from '@/utils/date-util'
import { formatMoney } from '@/utils/money-util'

const testId = 'legend-preview-computations'

export interface PreviewComputationsContainerProps {
  vm: LegendPreviewViewModel
}

export const PreviewComputationsContainer = observer(
  function PreviewComputationsContainer({
    vm,
  }: PreviewComputationsContainerProps) {
    const computation = vm.computation
    const pricing = vm.pricing
    if (!computation || !pricing) {
      return null
    }
    return (
      <>
        {pricing.bundleName && (
          <PreviewOutput title="Bundle">
            <PreviewValueText tone="Other" isLarge>
              {pricing.bundleName}
            </PreviewValueText>
          </PreviewOutput>
        )}
        {vm.hasConflictingReviewItems && (
          <PreviewOutput title="Pricing">
            <PreviewNotice title="Prep and review pricing error">
              Both the review and the prep and review items are in the inventory
              items. Select either the review or the prep and review item.
            </PreviewNotice>
          </PreviewOutput>
        )}
        {!vm.hasConflictingReviewItems && (
          <>
            <PreviewOutput
              title={
                pricing.usesReviewPricing
                  ? 'Review client price'
                  : 'Client price'
              }
              testId={`${testId}-client-price`}
            >
              <PreviewValueText tone="Other" isLarge>
                {formatMoney(pricing.price)}
              </PreviewValueText>
            </PreviewOutput>
            <PreviewOutput
              title={
                pricing.usesReviewPricing ? 'Review pro payout' : 'Pro payout'
              }
              testId={`${testId}-pro-payout`}
            >
              <PreviewValueText tone="Other" isLarge>
                {formatMoney(pricing.proPrice)}
              </PreviewValueText>
            </PreviewOutput>
          </>
        )}
        <PreviewOutput title="Route matching">
          <PreviewMatchList>
            <PreviewMatch
              title="Qualifying requirements"
              description="Candidate must match these at all times."
              isMatch={computation.routingMatch.matchesRequirements}
            />
          </PreviewMatchList>
          {computation.routingMatch.plans.length > 0 && (
            <PreviewMatchList>
              {computation.routingMatch.plans.map((plan) => (
                <PreviewMatch
                  key={plan.id}
                  title={plan.label}
                  description={plan.description}
                  isMatch={plan.matches}
                  testId={`${testId}-plan-${plan.id}`}
                />
              ))}
            </PreviewMatchList>
          )}
        </PreviewOutput>
        {computation.deadline && (
          <PreviewOutput title="Deadline">
            <PreviewValueText tone="Other" isLarge>
              {dateFormatters.dateLongWithTime.format(computation.deadline)}
            </PreviewValueText>
          </PreviewOutput>
        )}
        {computation.title && (
          <PreviewOutput title="Title">
            <PreviewValueText tone="Other" isLarge>
              {computation.title}
            </PreviewValueText>
          </PreviewOutput>
        )}
        {computation.specs.length > 0 && (
          <PreviewOutput title="Specs">
            {computation.specs.map((spec, i) => (
              <PreviewLine key={i} title={spec} isCompact />
            ))}
          </PreviewOutput>
        )}
        {computation.infoLines.length > 0 && (
          <PreviewOutput title="Info lines">
            {computation.infoLines.map((line, i) => (
              <PreviewLine key={i} title={line.title}>
                {line.content}
              </PreviewLine>
            ))}
          </PreviewOutput>
        )}
        {pricing.inventoryItems.length > 0 && (
          <PreviewOutput title="Inventory items">
            {pricing.inventoryItems.map((item) => (
              <PreviewLine key={item.id} title={`${item.name} ${item.shortId}`}>
                <PreviewStat>Quantity: {item.quantity}</PreviewStat>
                <PreviewStat>
                  Courtesy: {item.courtesyQuantity}
                  {item.courtesyMultiplier != null &&
                    ` (multiplier: ${item.courtesyMultiplier})`}
                </PreviewStat>
              </PreviewLine>
            ))}
          </PreviewOutput>
        )}
        {vm.organizerPrefill.length > 0 && (
          <PreviewOutput title="Organizer prefill">
            {vm.organizerPrefill.map((entry) => (
              <PreviewLine key={entry.bindingId} title={entry.title} isCompact>
                {entry.value}
              </PreviewLine>
            ))}
          </PreviewOutput>
        )}
      </>
    )
  },
)
